// 생성물 — 직접 고치지 않는다.
// 원본: design-system/icons.figma.json (Figma Icons 페이지 2163:2 에서 SVG 로 내보낸 것)
// 다시 만들기: npm run icons:build · 검사: npm run icons:check (scripts/design-system/build-icons.mjs)

/** 아이콘 path 하나. 색은 렌더러가 currentColor 로 칠한다(선 = stroke, 면 = fill) */
export interface IconGlyphPath {
  readonly d: string;
  readonly paint: 'stroke' | 'fill';
  readonly fillRule?: 'evenodd';
  /** 선 굵기(화면 px)가 기본 1.5 와 다를 때만(예외 목록 — build-icons.mjs STROKE_WIDTH_EXCEPTIONS) */
  readonly strokeWidth?: number;
}

/** Figma `Icon/*` 73개(Icons 페이지 순서) */
export const ICON_NAMES = [
  'chevron-up',
  'chevron-down',
  'chevron-left',
  'chevron-right',
  'undo',
  'close',
  'plus',
  'plus-thin',
  'minus',
  'check',
  'search',
  'search-circle',
  'download',
  'plus-circle',
  'minus-circle',
  'check-circle',
  'close-circle',
  'help-circle',
  'sort-asc',
  'sort-desc',
  'file-edit',
  'sort',
  'eye',
  'eye-off',
  'upload',
  'copy',
  'share',
  'trash',
  'user',
  'users',
  'settings',
  'bell',
  'bell-dot',
  'lock',
  'mail',
  'phone',
  'file',
  'chart-bar',
  'toggle-left',
  'impersonate',
  'trend-up',
  'trend-down',
  'trend-flat',
  'chart-bars-3',
  'pie-chart',
  'file-doc',
  'file-xls',
  'file-ppt',
  'file-pdf',
  'file-img',
  'file-etc',
  'stat-company',
  'stat-multiple',
  'stat-employee',
  'stat-investable',
  'stat-fund-volume',
  'stat-celebrate',
  'stat-presentation',
  'stat-aum',
  'stat-invest-total',
  'stat-exit',
  'stat-valuation',
  'info',
  'alert-circle',
  'alert-triangle',
  'inbox',
  'image',
  'clock',
  'bold',
  'italic',
  'list-bullet',
  'list-number',
  'link',
] as const;
export type IconName = (typeof ICON_NAMES)[number];

/** Figma Icon Library 의 묶음(grid/*) */
export const ICON_GROUPS = {
  nav: ['chevron-up', 'chevron-down', 'chevron-left', 'chevron-right', 'undo'],
  action: [
    'close',
    'plus',
    'plus-thin',
    'minus',
    'check',
    'search',
    'search-circle',
    'download',
    'plus-circle',
    'minus-circle',
    'check-circle',
    'close-circle',
    'help-circle',
    'sort-asc',
    'sort-desc',
    'file-edit',
    'sort',
    'eye',
    'eye-off',
    'upload',
    'copy',
    'share',
    'trash',
  ],
  domain: [
    'user',
    'users',
    'settings',
    'bell',
    'bell-dot',
    'lock',
    'mail',
    'phone',
    'file',
    'chart-bar',
    'toggle-left',
    'impersonate',
  ],
  trend: ['trend-up', 'trend-down', 'trend-flat', 'chart-bars-3', 'pie-chart'],
  'file-type': ['file-doc', 'file-xls', 'file-ppt', 'file-pdf', 'file-img', 'file-etc'],
  stat: [
    'stat-company',
    'stat-multiple',
    'stat-employee',
    'stat-investable',
    'stat-fund-volume',
    'stat-celebrate',
    'stat-presentation',
    'stat-aum',
    'stat-invest-total',
    'stat-exit',
    'stat-valuation',
  ],
  feedback: ['info', 'alert-circle', 'alert-triangle', 'inbox'],
  object: ['image', 'clock'],
  format: ['bold', 'italic', 'list-bullet', 'list-number', 'link'],
} as const satisfies Record<string, readonly IconName[]>;
export type IconGroup = keyof typeof ICON_GROUPS;

/** viewBox 0 0 24 24 기준 path. 선은 1.5 · round(strokeWidth 가 있으면 그 값) */
export const ICON_GLYPHS: Record<IconName, readonly IconGlyphPath[]> = {
  'chevron-up': [{ paint: 'stroke', d: 'M6 15L12 9L18 15' }],
  'chevron-down': [{ paint: 'stroke', d: 'M6 9L12 15L18 9' }],
  'chevron-left': [{ paint: 'stroke', d: 'M15 6L9 12L15 18' }],
  'chevron-right': [{ paint: 'stroke', d: 'M9 6L15 12L9 18' }],
  undo: [
    { paint: 'stroke', d: 'M7.5 5.5L4 9L7.5 12.5' },
    {
      paint: 'stroke',
      d: 'M4 9H14.5C15.6935 9 16.8381 9.47411 17.682 10.318C18.5259 11.1619 19 12.3065 19 13.5C19 14.6935 18.5259 15.8381 17.682 16.682C16.8381 17.5259 15.6935 18 14.5 18H8',
    },
  ],
  close: [
    { paint: 'stroke', d: 'M6 6L18 18' },
    { paint: 'stroke', d: 'M18 6L6 18' },
  ],
  plus: [
    { paint: 'stroke', d: 'M12 5V19' },
    { paint: 'stroke', d: 'M5 12H19' },
  ],
  'plus-thin': [
    { paint: 'stroke', strokeWidth: 0.75, d: 'M12 5V19' },
    { paint: 'stroke', strokeWidth: 0.75, d: 'M5 12H19' },
  ],
  minus: [{ paint: 'stroke', d: 'M5 12H19' }],
  check: [{ paint: 'stroke', d: 'M5 12.5L9.5 17L19 7' }],
  search: [
    {
      paint: 'stroke',
      d: 'M11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18Z',
    },
    { paint: 'stroke', d: 'M20.5 20.5L15.9 15.9' },
  ],
  'search-circle': [
    {
      paint: 'stroke',
      d: 'M12 21.5C17.2467 21.5 21.5 17.2467 21.5 12C21.5 6.75329 17.2467 2.5 12 2.5C6.75329 2.5 2.5 6.75329 2.5 12C2.5 17.2467 6.75329 21.5 12 21.5Z',
    },
    {
      paint: 'stroke',
      d: 'M11 14.4C12.9882 14.4 14.6 12.7882 14.6 10.8C14.6 8.81179 12.9882 7.20001 11 7.20001C9.0118 7.20001 7.40002 8.81179 7.40002 10.8C7.40002 12.7882 9.0118 14.4 11 14.4Z',
    },
    { paint: 'stroke', d: 'M13.6 13.4L16.5 16.6' },
  ],
  download: [
    { paint: 'stroke', d: 'M12 3.5V15' },
    { paint: 'stroke', d: 'M7.5 10.5L12 15L16.5 10.5' },
    {
      paint: 'stroke',
      d: 'M4 17.5V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V17.5',
    },
  ],
  'plus-circle': [
    {
      paint: 'stroke',
      d: 'M12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25Z',
    },
    { paint: 'stroke', d: 'M12 8.25V15.75' },
    { paint: 'stroke', d: 'M8.25 12H15.75' },
  ],
  'minus-circle': [
    {
      paint: 'stroke',
      d: 'M12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25Z',
    },
    { paint: 'stroke', d: 'M8.25 12H15.75' },
  ],
  'check-circle': [
    {
      paint: 'stroke',
      d: 'M12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25Z',
    },
    { paint: 'stroke', d: 'M8.29999 12.2L11 14.9L15.7 9.70001' },
  ],
  'close-circle': [
    {
      paint: 'stroke',
      d: 'M12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25Z',
    },
    { paint: 'stroke', d: 'M9.29999 9.29999L14.7 14.7' },
    { paint: 'stroke', d: 'M14.7 9.29999L9.29999 14.7' },
  ],
  'help-circle': [
    {
      paint: 'stroke',
      d: 'M12 20.25C16.5563 20.25 20.25 16.5563 20.25 12C20.25 7.44365 16.5563 3.75 12 3.75C7.44365 3.75 3.75 7.44365 3.75 12C3.75 16.5563 7.44365 20.25 12 20.25Z',
    },
    {
      paint: 'stroke',
      d: 'M9.70001 9.49999C9.70082 9.05571 9.82493 8.62037 10.0585 8.24245C10.2921 7.86454 10.626 7.55888 11.0231 7.35953C11.4201 7.16017 11.8647 7.07493 12.3073 7.11331C12.75 7.15169 13.1732 7.31218 13.53 7.57691C13.8868 7.84163 14.1632 8.2002 14.3282 8.61268C14.4933 9.02516 14.5406 9.47537 14.4649 9.91316C14.3893 10.3509 14.1935 10.7591 13.8996 11.0923C13.6056 11.4254 13.225 11.6704 12.8 11.8C12 12.1 11.8 12.6 11.8 13.5',
    },
    { paint: 'stroke', d: 'M11.8 16.4H11.81' },
  ],
  'sort-asc': [
    { paint: 'stroke', d: 'M6 20.5V5.5' },
    { paint: 'stroke', d: 'M3 8.5L6 5.5L9 8.5' },
    { paint: 'stroke', d: 'M12.5 7.5H16' },
    { paint: 'stroke', d: 'M12.5 12.5H18' },
    { paint: 'stroke', d: 'M12.5 17.5H20.5' },
  ],
  'sort-desc': [
    { paint: 'stroke', d: 'M6 3.5V18.5' },
    { paint: 'stroke', d: 'M3 15.5L6 18.5L9 15.5' },
    { paint: 'stroke', d: 'M12.5 7.5H20.5' },
    { paint: 'stroke', d: 'M12.5 12.5H18' },
    { paint: 'stroke', d: 'M12.5 17.5H16' },
  ],
  'file-edit': [
    {
      paint: 'stroke',
      d: 'M13.2 3.5H6.8C6.32261 3.5 5.86477 3.68964 5.52721 4.02721C5.18964 4.36477 5 4.82261 5 5.3V18.7C5 19.1774 5.18964 19.6352 5.52721 19.9728C5.86477 20.3104 6.32261 20.5 6.8 20.5H12',
    },
    { paint: 'stroke', d: 'M13.2 3.5L18.5 8.8' },
    { paint: 'stroke', d: 'M13.2 3.5V8.8H18.5' },
    { paint: 'stroke', d: 'M8.3 12H13.3' },
    { paint: 'stroke', d: 'M8.3 15.5H11.3' },
    {
      paint: 'stroke',
      d: 'M18.6 13.4C18.8889 13.1833 19.2462 13.0782 19.6063 13.1038C19.9665 13.1293 20.3053 13.284 20.5607 13.5393C20.816 13.7946 20.9706 14.1335 20.9962 14.4936C21.0218 14.8538 20.9166 15.2111 20.7 15.5L15.5 20.7L12.7 21.4L13.4 18.6L18.6 13.4Z',
    },
  ],
  sort: [
    { paint: 'stroke', d: 'M8 10L12 6L16 10' },
    { paint: 'stroke', d: 'M8 14L12 18L16 14' },
  ],
  eye: [
    {
      paint: 'stroke',
      d: 'M2.31199 12.33C2.22799 12.118 2.22799 11.882 2.31199 11.67C3.73899 7.53903 7.62899 4.76703 12 4.76703C16.371 4.76703 20.261 7.53903 21.688 11.67C21.772 11.882 21.772 12.118 21.688 12.33C20.261 16.461 16.371 19.233 12 19.233C7.62899 19.233 3.73899 16.461 2.31199 12.33Z',
    },
    {
      paint: 'stroke',
      d: 'M15 12C15 13.657 13.657 15 12 15C10.343 15 9 13.657 9 12C9 10.343 10.343 9 12 9C13.657 9 15 10.343 15 12Z',
    },
  ],
  'eye-off': [
    {
      paint: 'stroke',
      d: 'M10.733 5.326C15.41 4.664 19.935 7.284 21.688 11.67C21.772 11.882 21.772 12.118 21.688 12.33C21.353 13.164 20.91 13.951 20.37 14.67',
    },
    {
      paint: 'stroke',
      d: 'M14.084 14.158C12.907 15.295 11.036 15.279 9.879 14.121C8.721 12.964 8.705 11.093 9.842 9.91602',
    },
    {
      paint: 'stroke',
      d: 'M17.36 17.37C14.758 19.009 11.557 19.393 8.64099 18.417C5.72499 17.44 3.40199 15.206 2.31199 12.33C2.22799 12.118 2.22799 11.882 2.31199 11.67C3.17199 9.604 4.68499 7.876 6.61999 6.75',
    },
    { paint: 'stroke', d: 'M3.25 3.25L20.75 20.75' },
  ],
  upload: [
    { paint: 'stroke', d: 'M12 15V3.5' },
    { paint: 'stroke', d: 'M7.5 8L12 3.5L16.5 8' },
    { paint: 'stroke', d: 'M4 17.5V19C4 20.105 4.895 21 6 21H18C19.105 21 20 20.105 20 19V17.5' },
  ],
  copy: [
    {
      paint: 'stroke',
      d: 'M10.75 8.75H18.25C19.355 8.75 20.25 9.645 20.25 10.75V18.25C20.25 19.355 19.355 20.25 18.25 20.25H10.75C9.645 20.25 8.75 19.355 8.75 18.25V10.75C8.75 9.645 9.645 8.75 10.75 8.75Z',
    },
    {
      paint: 'stroke',
      d: 'M15.25 8.75V5.75C15.25 4.645 14.355 3.75 13.25 3.75H5.75C4.645 3.75 3.75 4.645 3.75 5.75V13.25C3.75 14.355 4.645 15.25 5.75 15.25H8.75',
    },
  ],
  share: [
    {
      paint: 'stroke',
      d: 'M20.25 5.75C20.25 7.269 19.019 8.5 17.5 8.5C15.981 8.5 14.75 7.269 14.75 5.75C14.75 4.231 15.981 3 17.5 3C19.019 3 20.25 4.231 20.25 5.75Z',
    },
    {
      paint: 'stroke',
      d: 'M9.25 12C9.25 13.519 8.019 14.75 6.5 14.75C4.981 14.75 3.75 13.519 3.75 12C3.75 10.481 4.981 9.25 6.5 9.25C8.019 9.25 9.25 10.481 9.25 12Z',
    },
    {
      paint: 'stroke',
      d: 'M20.25 18.25C20.25 19.769 19.019 21 17.5 21C15.981 21 14.75 19.769 14.75 18.25C14.75 16.731 15.981 15.5 17.5 15.5C19.019 15.5 20.25 16.731 20.25 18.25Z',
    },
    { paint: 'stroke', d: 'M8.89001 10.64L15.11 7.10999' },
    { paint: 'stroke', d: 'M8.89001 13.36L15.11 16.89' },
  ],
  trash: [
    { paint: 'stroke', d: 'M3.75 6.5H20.25' },
    {
      paint: 'stroke',
      d: 'M9 6.5V4.75C9 4.198 9.448 3.75 10 3.75H14C14.552 3.75 15 4.198 15 4.75V6.5',
    },
    {
      paint: 'stroke',
      d: 'M5.75 6.5L6.62 18.39C6.693 19.436 7.562 20.247 8.61 20.25H15.39C16.438 20.247 17.307 19.436 17.38 18.39L18.25 6.5',
    },
    { paint: 'stroke', d: 'M10 10.75V16' },
    { paint: 'stroke', d: 'M14 10.75V16' },
  ],
  user: [
    {
      paint: 'stroke',
      d: 'M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21',
    },
    {
      paint: 'stroke',
      d: 'M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z',
    },
  ],
  users: [
    {
      paint: 'stroke',
      d: 'M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21',
    },
    {
      paint: 'stroke',
      d: 'M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z',
    },
    {
      paint: 'stroke',
      d: 'M22 21V19C21.9993 18.1137 21.7044 17.2528 21.1614 16.5523C20.6184 15.8519 19.8581 15.3516 19 15.13',
    },
    {
      paint: 'stroke',
      d: 'M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88',
    },
  ],
  settings: [
    {
      paint: 'stroke',
      d: 'M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z',
    },
    {
      paint: 'stroke',
      d: 'M19.4 15C19.27 15.3 19.23 15.64 19.29 15.96C19.35 16.29 19.5 16.59 19.73 16.82L19.79 16.88C20.0814 17.1755 20.2787 17.5507 20.3571 17.9583C20.4354 18.3659 20.3912 18.7875 20.23 19.17C20.0701 19.5525 19.8005 19.8791 19.4553 20.1087C19.1101 20.3383 18.7046 20.4605 18.29 20.46C18.013 20.46 17.7388 20.4053 17.4831 20.2988C17.2273 20.1924 16.9952 20.0365 16.8 19.84L16.74 19.78C16.501 19.5412 16.1945 19.3815 15.8619 19.3225C15.5293 19.2635 15.1865 19.308 14.88 19.45C14.5842 19.5768 14.332 19.7872 14.1543 20.0555C13.9766 20.3238 13.8813 20.6382 13.88 20.96V21C13.88 21.5304 13.6693 22.0391 13.2942 22.4142C12.9191 22.7893 12.4104 23 11.88 23C11.3496 23 10.8409 22.7893 10.4658 22.4142C10.0907 22.0391 9.88 21.5304 9.88 21V20.91C9.88942 20.6012 9.81192 20.2959 9.65634 20.0289C9.50076 19.762 9.27335 19.544 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.82449 20.0814 6.44926 20.2787 6.04168 20.3571C5.63411 20.4354 5.21246 20.3912 4.83 20.23C4.44749 20.0701 4.12087 19.8005 3.89131 19.4553C3.66174 19.1101 3.53951 18.7046 3.54 18.29C3.54 17.73 3.76 17.2 4.15 16.8L4.21 16.74C4.4488 16.501 4.6085 16.1945 4.66752 15.8619C4.72653 15.5293 4.68202 15.1865 4.54 14.88C4.41324 14.5842 4.20276 14.332 3.93447 14.1543C3.66618 13.9766 3.35179 13.8813 3.03 13.88H3C2.46957 13.88 1.96086 13.6693 1.58579 13.2942C1.21071 12.9191 1 12.4104 1 11.88C1 11.3496 1.21071 10.8409 1.58579 10.4658C1.96086 10.0907 2.46957 9.88 3 9.88H3.09C3.39884 9.88942 3.70412 9.81192 3.97108 9.65634C4.23804 9.50076 4.45595 9.27335 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C3.91858 6.82449 3.72125 6.44926 3.64294 6.04168C3.56463 5.63411 3.60884 5.21246 3.77 4.83C3.92991 4.44749 4.19947 4.12087 4.54471 3.89131C4.88995 3.66174 5.29541 3.53951 5.71 3.54C6.27 3.54 6.8 3.76 7.2 4.15L7.26 4.21C7.49568 4.44054 7.79502 4.59519 8.11941 4.654C8.44381 4.71282 8.77838 4.67312 9.08 4.54H9C9.29577 4.41324 9.54802 4.20276 9.72569 3.93447C9.90337 3.66618 9.99872 3.35179 10 3.03V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.1755 3.91858 17.5507 3.72125 17.9583 3.64294C18.3659 3.56463 18.7875 3.60884 19.17 3.77C19.5525 3.92991 19.8791 4.19947 20.1087 4.54471C20.3383 4.88995 20.4605 5.29541 20.46 5.71C20.46 6.27 20.24 6.8 19.85 7.2L19.79 7.26C19.5595 7.49568 19.4048 7.79502 19.346 8.11941C19.2872 8.44381 19.3269 8.77838 19.46 9.08V9C19.72 9.6 20.31 10 20.97 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z',
    },
  ],
  bell: [
    {
      paint: 'stroke',
      d: 'M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z',
    },
    {
      paint: 'stroke',
      d: 'M13.7 21C13.5206 21.2894 13.2703 21.5283 12.9728 21.6939C12.6753 21.8595 12.3405 21.9464 12 21.9464C11.6595 21.9464 11.3246 21.8595 11.0271 21.6939C10.7296 21.5283 10.4794 21.2894 10.3 21',
    },
  ],
  'bell-dot': [
    {
      paint: 'stroke',
      d: 'M16.8 8.80002C16.8 7.36786 16.2311 5.99434 15.2184 4.98165C14.2057 3.96895 12.8322 3.40002 11.4 3.40002C9.96782 3.40002 8.59431 3.96895 7.58161 4.98165C6.56892 5.99434 5.99999 7.36786 5.99999 8.80002C5.99999 15.1 3.29999 16.9 3.29999 16.9H19.5C19.5 16.9 16.8 15.1 16.8 8.80002Z',
    },
    {
      paint: 'stroke',
      d: 'M12.9 20.6C12.7409 20.8695 12.5142 21.0929 12.2424 21.248C11.9706 21.4032 11.663 21.4848 11.35 21.4848C11.037 21.4848 10.7294 21.4032 10.4576 21.248C10.1858 21.0929 9.95912 20.8695 9.79999 20.6',
    },
    {
      paint: 'fill',
      d: 'M19.6 7.00001C20.9255 7.00001 22 5.9255 22 4.60001C22 3.27453 20.9255 2.20001 19.6 2.20001C18.2745 2.20001 17.2 3.27453 17.2 4.60001C17.2 5.9255 18.2745 7.00001 19.6 7.00001Z',
    },
  ],
  lock: [
    {
      paint: 'stroke',
      d: 'M6 10H18C18.5304 10 19.0391 10.2107 19.4142 10.5858C19.7893 10.9609 20 11.4696 20 12V19C20 19.5304 19.7893 20.0391 19.4142 20.4142C19.0391 20.7893 18.5304 21 18 21H6C5.46957 21 4.96086 20.7893 4.58579 20.4142C4.21071 20.0391 4 19.5304 4 19V12C4 11.4696 4.21071 10.9609 4.58579 10.5858C4.96086 10.2107 5.46957 10 6 10Z',
    },
    {
      paint: 'stroke',
      d: 'M7.5 9.99999V6.79999C7.5 5.60651 7.97411 4.46192 8.81802 3.61801C9.66193 2.77409 10.8065 2.29999 12 2.29999C13.1935 2.29999 14.3381 2.77409 15.182 3.61801C16.0259 4.46192 16.5 5.60651 16.5 6.79999V9.99999',
    },
    { paint: 'stroke', d: 'M12 14.5V17' },
  ],
  mail: [
    {
      paint: 'stroke',
      d: 'M4.5 5H19.5C20.0304 5 20.5391 5.21071 20.9142 5.58579C21.2893 5.96086 21.5 6.46957 21.5 7V17C21.5 17.5304 21.2893 18.0391 20.9142 18.4142C20.5391 18.7893 20.0304 19 19.5 19H4.5C3.96957 19 3.46086 18.7893 3.08579 18.4142C2.71071 18.0391 2.5 17.5304 2.5 17V7C2.5 6.46957 2.71071 5.96086 3.08579 5.58579C3.46086 5.21071 3.96957 5 4.5 5Z',
    },
    { paint: 'stroke', d: 'M21.5 7L12 13.8L2.5 7' },
  ],
  phone: [
    {
      paint: 'stroke',
      d: 'M20.5 16.9V19.3C20.5017 19.5411 20.452 19.7799 20.3544 20.0003C20.2567 20.2208 20.1133 20.418 19.9336 20.5787C19.7539 20.7395 19.5421 20.8602 19.3121 20.9329C19.0822 21.0055 18.8395 21.0284 18.6 21C16.0302 20.7061 13.5647 19.8158 11.4 18.4C9.38755 17.1193 7.68073 15.4125 6.4 13.4C4.98424 11.2354 4.0939 8.76981 3.8 6.20003C3.77163 5.96058 3.79452 5.71783 3.86715 5.4879C3.93979 5.25798 4.0605 5.04613 4.22128 4.86644C4.38206 4.68674 4.57923 4.5433 4.7997 4.44565C5.02017 4.34799 5.25888 4.29835 5.5 4.30003H7.9C8.31827 4.29713 8.72292 4.44852 9.03656 4.72526C9.3502 5.002 9.5508 5.38466 9.6 5.80003C9.7 6.60003 9.9 7.40003 10.2 8.10003C10.3135 8.40675 10.3362 8.7397 10.2652 9.05897C10.1943 9.37824 10.0328 9.67027 9.8 9.90003L8.7 11C9.88497 13.0873 11.6127 14.8151 13.7 16L14.8 14.9C15.0298 14.6673 15.3218 14.5057 15.6411 14.4348C15.9603 14.3638 16.2933 14.3865 16.6 14.5C17.3 14.8 18.1 15 18.9 15.1C19.1234 15.1128 19.342 15.1695 19.5435 15.2669C19.7449 15.3644 19.9251 15.5006 20.0737 15.6678C20.2224 15.8351 20.3365 16.03 20.4097 16.2414C20.4828 16.4529 20.5135 16.6767 20.5 16.9Z',
    },
  ],
  file: [
    {
      paint: 'stroke',
      d: 'M13 2.5H6.5C5.96957 2.5 5.46086 2.71071 5.08579 3.08579C4.71071 3.46086 4.5 3.96957 4.5 4.5V19.5C4.5 20.0304 4.71071 20.5391 5.08579 20.9142C5.46086 21.2893 5.96957 21.5 6.5 21.5H17.5C18.0304 21.5 18.5391 21.2893 18.9142 20.9142C19.2893 20.5391 19.5 20.0304 19.5 19.5V9L13 2.5Z',
    },
    { paint: 'stroke', d: 'M13 2.5V9H19.5' },
  ],
  'chart-bar': [
    {
      paint: 'stroke',
      d: 'M4 21V12.5C4 12.2348 4.10536 11.9804 4.29289 11.7929C4.48043 11.6054 4.73478 11.5 5 11.5H8C8.26522 11.5 8.51957 11.6054 8.70711 11.7929C8.89464 11.9804 9 12.2348 9 12.5V21',
    },
    {
      paint: 'stroke',
      d: 'M10 21V4C10 3.73478 10.1054 3.48043 10.2929 3.29289C10.4804 3.10536 10.7348 3 11 3H13C13.2652 3 13.5196 3.10536 13.7071 3.29289C13.8946 3.48043 14 3.73478 14 4V21',
    },
    {
      paint: 'stroke',
      d: 'M15 21V15C15 14.7348 15.1054 14.4804 15.2929 14.2929C15.4804 14.1054 15.7348 14 16 14H19C19.2652 14 19.5196 14.1054 19.7071 14.2929C19.8946 14.4804 20 14.7348 20 15V21',
    },
    { paint: 'stroke', d: 'M2.5 21H21.5' },
  ],
  'toggle-left': [
    {
      paint: 'stroke',
      d: 'M16 5H8C6.14348 5 4.36301 5.7375 3.05025 7.05025C1.7375 8.36301 1 10.1435 1 12C1 13.8565 1.7375 15.637 3.05025 16.9497C4.36301 18.2625 6.14348 19 8 19H16C17.8565 19 19.637 18.2625 20.9497 16.9497C22.2625 15.637 23 13.8565 23 12C23 10.1435 22.2625 8.36301 20.9497 7.05025C19.637 5.7375 17.8565 5 16 5Z',
    },
    {
      paint: 'stroke',
      d: 'M8 15C9.65685 15 11 13.6569 11 12C11 10.3431 9.65685 9 8 9C6.34315 9 5 10.3431 5 12C5 13.6569 6.34315 15 8 15Z',
    },
  ],
  impersonate: [
    {
      paint: 'stroke',
      d: 'M12 12C14.2091 12 16 10.2091 16 8C16 5.79086 14.2091 4 12 4C9.79086 4 8 5.79086 8 8C8 10.2091 9.79086 12 12 12Z',
    },
    {
      paint: 'stroke',
      d: 'M4 21V20C4 18.4087 4.63214 16.8826 5.75736 15.7574C6.88258 14.6321 8.4087 14 10 14H14C15.5913 14 17.1174 14.6321 18.2426 15.7574C19.3679 16.8826 20 18.4087 20 20V21',
    },
    { paint: 'stroke', d: 'M16 3L19 6L16 9' },
  ],
  'trend-up': [{ paint: 'fill', d: 'M12 6L19 18H5L12 6Z' }],
  'trend-down': [{ paint: 'fill', d: 'M12 18L5 6H19L12 18Z' }],
  'trend-flat': [{ paint: 'stroke', d: 'M7 12H17' }],
  'chart-bars-3': [
    { paint: 'stroke', d: 'M12 20.5V11.5' },
    { paint: 'stroke', d: 'M17 20.5V4.5' },
    { paint: 'stroke', d: 'M7 20.5V15.5' },
  ],
  'pie-chart': [
    {
      paint: 'stroke',
      d: 'M12 3V12H21C21 9.61305 20.0518 7.32387 18.364 5.63604C16.6761 3.94821 14.3869 3 12 3Z',
    },
    {
      paint: 'stroke',
      d: 'M20.3 15.4C19.7355 16.801 18.8274 18.0374 17.6594 18.9952C16.4915 19.953 15.1012 20.6014 13.6168 20.8808C12.1324 21.1602 10.6016 21.0614 9.16537 20.5937C7.72915 20.1259 6.43375 19.3043 5.39853 18.2043C4.36331 17.1044 3.62157 15.7616 3.24166 14.2997C2.86175 12.8378 2.85589 11.3038 3.22461 9.83905C3.59333 8.37428 4.32478 7.02587 5.35156 5.91807C6.37834 4.81027 7.66742 3.9787 9.10002 3.5',
    },
  ],
  'file-doc': [
    {
      paint: 'stroke',
      d: 'M13 2.5H6.5C5.96957 2.5 5.46086 2.71071 5.08579 3.08579C4.71071 3.46086 4.5 3.96957 4.5 4.5V19.5C4.5 20.0304 4.71071 20.5391 5.08579 20.9142C5.46086 21.2893 5.96957 21.5 6.5 21.5H17.5C18.0304 21.5 18.5391 21.2893 18.9142 20.9142C19.2893 20.5391 19.5 20.0304 19.5 19.5V9L13 2.5Z',
    },
    { paint: 'stroke', d: 'M13 2.5V9H19.5' },
    {
      paint: 'fill',
      d: 'M7.58253 20.1999H6.10384V15.8363H7.61236C8.0456 15.8363 8.41776 15.9236 8.72884 16.0983C9.04134 16.2716 9.28139 16.5209 9.44901 16.8462C9.61662 17.1715 9.70043 17.5607 9.70043 18.0138C9.70043 18.4684 9.61591 18.859 9.44688 19.1857C9.27926 19.5124 9.03707 19.7631 8.72031 19.9378C8.40497 20.1125 8.02571 20.1999 7.58253 20.1999ZM6.89432 19.516H7.54418C7.84815 19.516 8.10171 19.4606 8.30483 19.3498C8.50796 19.2375 8.66065 19.0706 8.76293 18.8491C8.8652 18.626 8.91634 18.3476 8.91634 18.0138C8.91634 17.68 8.8652 17.403 8.76293 17.1829C8.66065 16.9613 8.50938 16.7958 8.30909 16.6864C8.11023 16.5756 7.86307 16.5202 7.56761 16.5202H6.89432V19.516Z',
    },
    {
      paint: 'fill',
      d: 'M13.9506 18.0181C13.9506 18.4883 13.8626 18.891 13.6864 19.2262C13.5117 19.56 13.2731 19.8157 12.9705 19.9932C12.6694 20.1708 12.3278 20.2596 11.9457 20.2596C11.5636 20.2596 11.2212 20.1708 10.9187 19.9932C10.6175 19.8143 10.3789 19.5579 10.2028 19.2241C10.0281 18.8888 9.9407 18.4868 9.9407 18.0181C9.9407 17.5479 10.0281 17.1459 10.2028 16.8121C10.3789 16.4769 10.6175 16.2205 10.9187 16.0429C11.2212 15.8654 11.5636 15.7766 11.9457 15.7766C12.3278 15.7766 12.6694 15.8654 12.9705 16.0429C13.2731 16.2205 13.5117 16.4769 13.6864 16.8121C13.8626 17.1459 13.9506 17.5479 13.9506 18.0181ZM13.1559 18.0181C13.1559 17.6871 13.104 17.408 13.0004 17.1807C12.8981 16.952 12.756 16.7795 12.5742 16.663C12.3924 16.5451 12.1829 16.4861 11.9457 16.4861C11.7085 16.4861 11.4989 16.5451 11.3171 16.663C11.1353 16.7795 10.9925 16.952 10.8889 17.1807C10.7866 17.408 10.7354 17.6871 10.7354 18.0181C10.7354 18.3491 10.7866 18.6289 10.8889 18.8576C10.9925 19.0849 11.1353 19.2574 11.3171 19.3753C11.4989 19.4918 11.7085 19.55 11.9457 19.55C12.1829 19.55 12.3924 19.4918 12.5742 19.3753C12.756 19.2574 12.8981 19.0849 13.0004 18.8576C13.104 18.6289 13.1559 18.3491 13.1559 18.0181Z',
    },
    {
      paint: 'fill',
      d: 'M18.0331 17.3086H17.2362C17.2135 17.1779 17.1716 17.0621 17.1105 16.9613C17.0494 16.859 16.9734 16.7723 16.8825 16.7013C16.7916 16.6303 16.6879 16.577 16.5714 16.5415C16.4564 16.5046 16.3321 16.4861 16.1985 16.4861C15.9613 16.4861 15.7511 16.5458 15.5679 16.6651C15.3846 16.783 15.2412 16.9563 15.1375 17.185C15.0338 17.4123 14.9819 17.69 14.9819 18.0181C14.9819 18.3519 15.0338 18.6331 15.1375 18.8618C15.2426 19.0891 15.386 19.261 15.5679 19.3775C15.7511 19.4925 15.9606 19.55 16.1964 19.55C16.3271 19.55 16.4493 19.533 16.5629 19.4989C16.6779 19.4634 16.7809 19.4116 16.8718 19.3434C16.9642 19.2752 17.0416 19.1914 17.1041 19.092C17.168 18.9925 17.212 18.8789 17.2362 18.751L18.0331 18.7553C18.0032 18.9627 17.9386 19.1573 17.8392 19.3391C17.7412 19.5209 17.6126 19.6814 17.4535 19.8206C17.2944 19.9584 17.1083 20.0664 16.8953 20.1445C16.6822 20.2212 16.4457 20.2596 16.1858 20.2596C15.8022 20.2596 15.4599 20.1708 15.1588 19.9932C14.8576 19.8157 14.6204 19.5593 14.4471 19.2241C14.2738 18.8888 14.1872 18.4868 14.1872 18.0181C14.1872 17.5479 14.2745 17.1459 14.4493 16.8121C14.624 16.4769 14.8619 16.2205 15.163 16.0429C15.4642 15.8654 15.8051 15.7766 16.1858 15.7766C16.4287 15.7766 16.6545 15.8107 16.8633 15.8789C17.0721 15.9471 17.2582 16.0472 17.4216 16.1793C17.5849 16.31 17.7191 16.4705 17.8243 16.6608C17.9308 16.8498 18.0004 17.0657 18.0331 17.3086Z',
    },
  ],
  'file-xls': [
    {
      paint: 'stroke',
      d: 'M13 2.5H6.5C5.96957 2.5 5.46086 2.71071 5.08579 3.08579C4.71071 3.46086 4.5 3.96957 4.5 4.5V19.5C4.5 20.0304 4.71071 20.5391 5.08579 20.9142C5.46086 21.2893 5.96957 21.5 6.5 21.5H17.5C18.0304 21.5 18.5391 21.2893 18.9142 20.9142C19.2893 20.5391 19.5 20.0304 19.5 19.5V9L13 2.5Z',
    },
    { paint: 'stroke', d: 'M13 2.5V9H19.5' },
    {
      paint: 'fill',
      d: 'M7.57215 15.8363L8.53309 17.4343H8.56718L9.53238 15.8363H10.4337L9.0892 18.0181L10.455 20.1999H9.53877L8.56718 18.6125H8.53309L7.5615 20.1999H6.64957L8.02812 18.0181L6.66661 15.8363H7.57215Z',
    },
    { paint: 'fill', d: 'M10.8542 20.1999V15.8363H11.6447V19.5373H13.5666V20.1999H10.8542Z' },
    {
      paint: 'fill',
      d: 'M16.4241 17.0358C16.4042 16.8498 16.3204 16.7049 16.1727 16.6012C16.0264 16.4975 15.836 16.4456 15.6017 16.4456C15.4369 16.4456 15.2956 16.4705 15.1777 16.5202C15.0598 16.5699 14.9696 16.6374 14.9071 16.7226C14.8446 16.8079 14.8126 16.9052 14.8112 17.0145C14.8112 17.1054 14.8318 17.1843 14.873 17.251C14.9156 17.3178 14.9731 17.3746 15.0456 17.4215C15.118 17.467 15.1983 17.5053 15.2863 17.5366C15.3744 17.5678 15.4632 17.5941 15.5527 17.6154L15.9617 17.7177C16.1265 17.756 16.2849 17.8079 16.4369 17.8732C16.5903 17.9385 16.7274 18.0209 16.8481 18.1204C16.9703 18.2198 17.0669 18.3398 17.1379 18.4804C17.2089 18.6211 17.2444 18.7858 17.2444 18.9748C17.2444 19.2304 17.1791 19.4556 17.0484 19.6502C16.9177 19.8434 16.7288 19.9947 16.4816 20.104C16.2359 20.212 15.9383 20.266 15.5889 20.266C15.2494 20.266 14.9546 20.2134 14.7046 20.1083C14.4561 20.0032 14.2615 19.8498 14.1208 19.6481C13.9816 19.4464 13.9063 19.2006 13.895 18.9108H14.6727C14.684 19.0628 14.7309 19.1893 14.8133 19.2901C14.8957 19.391 15.0029 19.4662 15.135 19.516C15.2686 19.5657 15.4177 19.5905 15.5825 19.5905C15.7544 19.5905 15.9049 19.565 16.0342 19.5138C16.1649 19.4613 16.2671 19.3888 16.341 19.2965C16.4149 19.2027 16.4525 19.0934 16.4539 18.9684C16.4525 18.8547 16.4191 18.761 16.3538 18.6871C16.2885 18.6118 16.1968 18.5493 16.0789 18.4996C15.9625 18.4485 15.8261 18.403 15.6698 18.3633L15.1734 18.2354C14.814 18.1431 14.5299 18.0032 14.3211 17.8157C14.1137 17.6268 14.01 17.376 14.01 17.0635C14.01 16.8064 14.0796 16.5813 14.2188 16.3881C14.3595 16.1949 14.5505 16.0451 14.792 15.9385C15.0335 15.8306 15.3069 15.7766 15.6123 15.7766C15.922 15.7766 16.1933 15.8306 16.4262 15.9385C16.6606 16.0451 16.8446 16.1935 16.9781 16.3839C17.1116 16.5728 17.1805 16.7901 17.1848 17.0358H16.4241Z',
    },
  ],
  'file-ppt': [
    {
      paint: 'stroke',
      d: 'M13 2.5H6.5C5.96957 2.5 5.46086 2.71071 5.08579 3.08579C4.71071 3.46086 4.5 3.96957 4.5 4.5V19.5C4.5 20.0304 4.71071 20.5391 5.08579 20.9142C5.46086 21.2893 5.96957 21.5 6.5 21.5H17.5C18.0304 21.5 18.5391 21.2893 18.9142 20.9142C19.2893 20.5391 19.5 20.0304 19.5 19.5V9L13 2.5Z',
    },
    { paint: 'stroke', d: 'M13 2.5V9H19.5' },
    {
      paint: 'fill',
      d: 'M6.7886 20.1999V15.8363H8.42497C8.7602 15.8363 9.04145 15.8988 9.26872 16.0238C9.49741 16.1488 9.67 16.3207 9.78647 16.5394C9.90437 16.7568 9.96332 17.0039 9.96332 17.2809C9.96332 17.5607 9.90437 17.8093 9.78647 18.0266C9.66858 18.244 9.49457 18.4151 9.26446 18.5401C9.03434 18.6637 8.75096 18.7255 8.41431 18.7255H7.3298V18.0757H8.30778C8.5038 18.0757 8.66431 18.0416 8.78931 17.9734C8.91432 17.9052 9.00664 17.8114 9.0663 17.6921C9.12738 17.5728 9.15792 17.4357 9.15792 17.2809C9.15792 17.1261 9.12738 16.9897 9.0663 16.8718C9.00664 16.7539 8.9136 16.6623 8.78718 16.597C8.66218 16.5302 8.50096 16.4968 8.30352 16.4968H7.57909V20.1999H6.7886Z',
    },
    {
      paint: 'fill',
      d: 'M10.4499 20.1999V15.8363H12.0863C12.4215 15.8363 12.7028 15.8988 12.93 16.0238C13.1587 16.1488 13.3313 16.3207 13.4478 16.5394C13.5657 16.7568 13.6246 17.0039 13.6246 17.2809C13.6246 17.5607 13.5657 17.8093 13.4478 18.0266C13.3299 18.244 13.1559 18.4151 12.9258 18.5401C12.6957 18.6637 12.4123 18.7255 12.0756 18.7255H10.9911V18.0757H11.9691C12.1651 18.0757 12.3256 18.0416 12.4506 17.9734C12.5756 17.9052 12.668 17.8114 12.7276 17.6921C12.7887 17.5728 12.8193 17.4357 12.8193 17.2809C12.8193 17.1261 12.7887 16.9897 12.7276 16.8718C12.668 16.7539 12.5749 16.6623 12.4485 16.597C12.3235 16.5302 12.1623 16.4968 11.9648 16.4968H11.2404V20.1999H10.4499Z',
    },
    {
      paint: 'fill',
      d: 'M13.9195 16.4989V15.8363H17.401V16.4989H16.0523V20.1999H15.2682V16.4989H13.9195Z',
    },
  ],
  'file-pdf': [
    {
      paint: 'stroke',
      d: 'M13 2.5H6.5C5.96957 2.5 5.46086 2.71071 5.08579 3.08579C4.71071 3.46086 4.5 3.96957 4.5 4.5V19.5C4.5 20.0304 4.71071 20.5391 5.08579 20.9142C5.46086 21.2893 5.96957 21.5 6.5 21.5H17.5C18.0304 21.5 18.5391 21.2893 18.9142 20.9142C19.2893 20.5391 19.5 20.0304 19.5 19.5V9L13 2.5Z',
    },
    { paint: 'stroke', d: 'M13 2.5V9H19.5' },
    {
      paint: 'fill',
      d: 'M6.7681 20.1999V15.8363H8.40446C8.73969 15.8363 9.02094 15.8988 9.24821 16.0238C9.4769 16.1488 9.64949 16.3207 9.76597 16.5394C9.88386 16.7568 9.94281 17.0039 9.94281 17.2809C9.94281 17.5607 9.88386 17.8093 9.76597 18.0266C9.64807 18.244 9.47406 18.4151 9.24395 18.5401C9.01384 18.6637 8.73045 18.7255 8.39381 18.7255H7.30929V18.0757H8.28727C8.4833 18.0757 8.64381 18.0416 8.76881 17.9734C8.89381 17.9052 8.98614 17.8114 9.0458 17.6921C9.10688 17.5728 9.13742 17.4357 9.13742 17.2809C9.13742 17.1261 9.10688 16.9897 9.0458 16.8718C8.98614 16.7539 8.8931 16.6623 8.76668 16.597C8.64168 16.5302 8.48045 16.4968 8.28301 16.4968H7.55858V20.1999H6.7681Z',
    },
    {
      paint: 'fill',
      d: 'M11.9081 20.1999H10.4294V15.8363H11.9379C12.3712 15.8363 12.7433 15.9237 13.0544 16.0984C13.3669 16.2717 13.607 16.521 13.7746 16.8462C13.9422 17.1715 14.026 17.5607 14.026 18.0139C14.026 18.4684 13.9415 18.859 13.7725 19.1857C13.6049 19.5124 13.3627 19.7632 13.0459 19.9379C12.7306 20.1126 12.3513 20.1999 11.9081 20.1999ZM11.2199 19.516H11.8698C12.1737 19.516 12.4273 19.4606 12.6304 19.3498C12.8335 19.2376 12.9862 19.0707 13.0885 18.8491C13.1908 18.6261 13.2419 18.3477 13.2419 18.0139C13.2419 17.6801 13.1908 17.4031 13.0885 17.1829C12.9862 16.9613 12.835 16.7958 12.6347 16.6864C12.4358 16.5757 12.1887 16.5203 11.8932 16.5203H11.2199V19.516Z',
    },
    {
      paint: 'fill',
      d: 'M14.5771 20.1999V15.8363H17.3725V16.4989H15.3676V17.6836H17.1808V18.3462H15.3676V20.1999H14.5771Z',
    },
  ],
  'file-img': [
    {
      paint: 'stroke',
      d: 'M13 2.5H6.5C5.96957 2.5 5.46086 2.71071 5.08579 3.08579C4.71071 3.46086 4.5 3.96957 4.5 4.5V19.5C4.5 20.0304 4.71071 20.5391 5.08579 20.9142C5.46086 21.2893 5.96957 21.5 6.5 21.5H17.5C18.0304 21.5 18.5391 21.2893 18.9142 20.9142C19.2893 20.5391 19.5 20.0304 19.5 19.5V9L13 2.5Z',
    },
    { paint: 'stroke', d: 'M13 2.5V9H19.5' },
    { paint: 'fill', d: 'M7.61421 15.8363V20.1999H6.82373V15.8363H7.61421Z' },
    {
      paint: 'fill',
      d: 'M8.27021 15.8363H9.23754L10.533 18.9982H10.5841L11.8796 15.8363H12.8469V20.1999H12.0884V17.202H12.0479L10.8419 20.1871H10.2752L9.06922 17.1956H9.02874V20.1999H8.27021V15.8363Z',
    },
    {
      paint: 'fill',
      d: 'M16.431 17.2297C16.3955 17.1147 16.3465 17.0117 16.284 16.9208C16.2229 16.8285 16.1491 16.7496 16.0624 16.6843C15.9772 16.6189 15.8792 16.5699 15.7684 16.5373C15.6576 16.5032 15.5368 16.4861 15.4062 16.4861C15.1718 16.4861 14.963 16.5451 14.7797 16.663C14.5965 16.7809 14.4523 16.9542 14.3472 17.1829C14.2435 17.4101 14.1917 17.6871 14.1917 18.0138C14.1917 18.3434 14.2435 18.6225 14.3472 18.8512C14.4509 19.0799 14.5951 19.2539 14.7797 19.3732C14.9644 19.4911 15.1789 19.55 15.4232 19.55C15.6448 19.55 15.8365 19.5074 15.9985 19.4222C16.1618 19.337 16.2875 19.2162 16.3756 19.06C16.4637 18.9023 16.5077 18.7177 16.5077 18.506L16.6867 18.5337H15.502V17.9158H17.2726V18.44C17.2726 18.8135 17.1931 19.1367 17.034 19.4094C16.8749 19.6822 16.6562 19.8924 16.3777 20.0401C16.0993 20.1864 15.7797 20.2596 15.4189 20.2596C15.0169 20.2596 14.664 20.1694 14.36 19.989C14.0574 19.8072 13.8209 19.5493 13.6505 19.2155C13.4814 18.8803 13.3969 18.4826 13.3969 18.0224C13.3969 17.6701 13.4466 17.3554 13.5461 17.0785C13.6469 16.8015 13.7875 16.5664 13.9679 16.3732C14.1483 16.1786 14.36 16.0309 14.6029 15.93C14.8458 15.8277 15.11 15.7766 15.3955 15.7766C15.637 15.7766 15.8621 15.8121 16.0709 15.8831C16.2797 15.9527 16.4651 16.0522 16.627 16.1814C16.7904 16.3107 16.9246 16.4641 17.0297 16.6417C17.1348 16.8192 17.2037 17.0152 17.2364 17.2297H16.431Z',
    },
  ],
  'file-etc': [
    {
      paint: 'stroke',
      d: 'M13 2.5H6.5C5.96957 2.5 5.46086 2.71071 5.08579 3.08579C4.71071 3.46086 4.5 3.96957 4.5 4.5V19.5C4.5 20.0304 4.71071 20.5391 5.08579 20.9142C5.46086 21.2893 5.96957 21.5 6.5 21.5H17.5C18.0304 21.5 18.5391 21.2893 18.9142 20.9142C19.2893 20.5391 19.5 20.0304 19.5 19.5V9L13 2.5Z',
    },
    { paint: 'stroke', d: 'M13 2.5V9H19.5' },
    {
      paint: 'fill',
      d: 'M6.69482 20.1999V15.8363H9.53289V16.4989H7.48531V17.6836H9.38588V18.3462H7.48531V19.5373H9.54994V20.1999H6.69482Z',
    },
    {
      paint: 'fill',
      d: 'M9.94759 16.4989V15.8363H13.4291V16.4989H12.0804V20.1999H11.2963V16.4989H9.94759Z',
    },
    {
      paint: 'fill',
      d: 'M17.442 17.3086H16.6451C16.6224 17.1779 16.5805 17.0621 16.5194 16.9613C16.4584 16.859 16.3824 16.7723 16.2915 16.7013C16.2005 16.6303 16.0968 16.577 15.9804 16.5415C15.8653 16.5046 15.741 16.4861 15.6075 16.4861C15.3703 16.4861 15.1601 16.5458 14.9768 16.6651C14.7936 16.783 14.6501 16.9563 14.5464 17.185C14.4427 17.4123 14.3909 17.69 14.3909 18.0181C14.3909 18.3519 14.4427 18.6331 14.5464 18.8618C14.6515 19.0891 14.795 19.261 14.9768 19.3775C15.1601 19.4925 15.3696 19.55 15.6054 19.55C15.7361 19.55 15.8582 19.533 15.9718 19.4989C16.0869 19.4634 16.1899 19.4116 16.2808 19.3434C16.3731 19.2752 16.4505 19.1914 16.513 19.092C16.577 18.9925 16.621 18.8789 16.6451 18.751L17.442 18.7553C17.4122 18.9627 17.3476 19.1573 17.2481 19.3391C17.1501 19.5209 17.0216 19.6814 16.8625 19.8206C16.7034 19.9584 16.5173 20.0664 16.3042 20.1445C16.0912 20.2212 15.8547 20.2596 15.5947 20.2596C15.2112 20.2596 14.8689 20.1708 14.5677 19.9932C14.2666 19.8157 14.0294 19.5593 13.8561 19.2241C13.6828 18.8888 13.5961 18.4868 13.5961 18.0181C13.5961 17.5479 13.6835 17.1459 13.8582 16.8121C14.0329 16.4769 14.2709 16.2205 14.572 16.0429C14.8731 15.8654 15.214 15.7766 15.5947 15.7766C15.8376 15.7766 16.0635 15.8107 16.2723 15.8789C16.4811 15.9471 16.6672 16.0472 16.8305 16.1793C16.9939 16.31 17.1281 16.4705 17.2332 16.6608C17.3397 16.8498 17.4093 17.0657 17.442 17.3086Z',
    },
  ],
  'stat-company': [
    { paint: 'stroke', d: 'M19.8605 22.25H16.7292V9.16443H19.8605V22.25Z' },
    { paint: 'stroke', d: 'M7.5717 22.2499H4.86883V12.0173H7.5717V22.2499Z' },
    { paint: 'stroke', d: 'M16.7292 22.2499H7.57173V3.66467H16.7292V22.2499Z' },
    { paint: 'stroke', d: 'M15.4248 3.66461H8.87605V1.74976H15.4248V3.66461Z' },
    { paint: 'stroke', d: 'M15.0004 7.85258H13.0809V5.92993H15.0004V7.85258Z' },
    { paint: 'stroke', d: 'M11.22 7.85258H9.30051V5.92993H11.22V7.85258Z' },
    { paint: 'stroke', d: 'M15.0004 12.085H13.0809V10.1624H15.0004V12.085Z' },
    { paint: 'stroke', d: 'M11.22 12.085H9.30051V10.1624H11.22V12.085Z' },
    { paint: 'stroke', d: 'M15.0004 16.3174H13.0809V14.3948H15.0004V16.3174Z' },
    { paint: 'stroke', d: 'M11.22 16.3174H9.30051V14.3948H11.22V16.3174Z' },
    { paint: 'stroke', d: 'M3.3354 22.2499H20.6646' },
    { paint: 'stroke', d: 'M13.8217 22.25H10.4792V19.0842H13.8217V22.25Z' },
  ],
  'stat-multiple': [
    {
      paint: 'stroke',
      d: 'M14.5103 7.39772C14.5103 9.36173 12.9181 10.9539 10.9541 10.9539C8.99004 10.9539 7.3979 9.36173 7.3979 7.39772C7.3979 5.4337 8.99004 3.84155 10.9541 3.84155C12.9181 3.84155 14.5103 5.4337 14.5103 7.39772Z',
    },
    {
      paint: 'stroke',
      d: 'M18.694 3.84167H17.8572V18.9031C17.8572 19.5963 17.2953 20.1582 16.6021 20.1582H6.14276V20.9949C6.14276 21.6882 6.70472 22.2501 7.39786 22.2501H18.694C19.3871 22.2501 19.949 21.6882 19.949 20.9949V5.09682C19.949 4.4036 19.3871 3.84167 18.694 3.84167Z',
    },
    { paint: 'stroke', d: 'M9.13412 7.24615L10.337 8.44901L12.4393 6.34668' },
    { paint: 'stroke', d: 'M6.14279 13.8827H11.5816' },
    { paint: 'stroke', d: 'M6.14279 15.9746H15.7653' },
    { paint: 'stroke', d: 'M6.14279 18.0664H15.7653' },
    {
      paint: 'stroke',
      d: 'M16.6021 1.74988H5.306C4.61287 1.74988 4.0509 2.3118 4.0509 3.00502V18.9031C4.0509 19.5963 4.61287 20.1583 5.306 20.1583H16.6021C17.2953 20.1583 17.8572 19.5963 17.8572 18.9031V3.00502C17.8572 2.3118 17.2953 1.74988 16.6021 1.74988Z',
    },
  ],
  'stat-employee': [
    { paint: 'stroke', d: 'M11.1666 22.2501V10.7113H7.83322V22.2501L11.1666 22.2501Z' },
    { paint: 'stroke', d: 'M17.8334 9.00293V22.2501L21.1667 22.2502V9.00293H17.8334Z' },
    { paint: 'stroke', d: 'M1.74979 22.2501H22.2502' },
    { paint: 'stroke', d: 'M1.74979 12.4467L9.06343 5.13304L13.9652 10.0348L21.9584 2.0415' },
    { paint: 'stroke', d: 'M22.2502 3.41657V1.74988H20.5834' },
    { paint: 'stroke', d: 'M6.16654 22.0418V15.3363H2.83316V22.0418' },
    { paint: 'stroke', d: 'M16.1667 22.0418V12.6697H12.8333V22.0418' },
  ],
  'stat-investable': [
    {
      paint: 'stroke',
      d: 'M11.7032 8.80587C11.4706 8.47434 10.8855 8.22795 10.1209 8.45834C9.41878 8.66976 9.10419 9.79333 10.02 10.2251C10.1636 10.2929 10.5318 10.4375 11.0874 10.6339C12.3308 11.0734 12.3716 12.7064 10.8959 12.8354C10.4042 12.8782 9.70302 12.7461 9.3299 12.2829',
    },
    { paint: 'stroke', d: 'M10.6103 7.88904V13.2672' },
    {
      paint: 'stroke',
      d: 'M16.8205 4.33572C20.2682 7.78334 20.2682 13.3731 16.8205 16.8207C13.373 20.2683 7.78322 20.2683 4.3356 16.8207C0.887971 13.3731 0.887971 7.78334 4.3356 4.33572C7.78322 0.888093 13.373 0.888093 16.8205 4.33572Z',
    },
    {
      paint: 'stroke',
      d: 'M10.578 17.573C8.70964 17.573 6.95314 16.8455 5.63196 15.5243C4.31082 14.2032 3.58322 12.4466 3.58322 10.5782C3.58322 8.70978 4.31082 6.95325 5.63196 5.63211C6.95314 4.31096 8.70964 3.58337 10.578 3.58337C12.4464 3.58337 14.203 4.31096 15.5241 5.63211C16.8453 6.95325 17.5729 8.70978 17.5729 10.5782C17.5729 12.4466 16.8453 14.2032 15.5242 15.5243C14.203 16.8455 12.4464 17.573 10.578 17.573Z',
    },
    { paint: 'stroke', d: 'M16.8205 16.8206L18.2865 18.2865' },
    {
      paint: 'stroke',
      d: 'M21.9243 21.9244C21.49 22.3587 20.7857 22.3587 20.3514 21.9244L17.6287 19.2017L19.2017 17.6288L21.9243 20.3515C22.3587 20.7857 22.3587 21.49 21.9243 21.9244Z',
    },
  ],
  'stat-fund-volume': [
    { paint: 'stroke', d: 'M22.2502 15.068H15.2179V17.1324H22.2502V15.068Z' },
    { paint: 'stroke', d: 'M8.78212 15.068H1.74988V17.1324H8.78212V15.068Z' },
    { paint: 'stroke', d: 'M8.7822 6.68713H3.63116V13.184H8.7822V6.68713Z' },
    { paint: 'stroke', d: 'M20.369 6.68713H15.2179V13.184H20.369V6.68713Z' },
    { paint: 'stroke', d: 'M15.2178 4.8031H8.7821V19.1969H15.2178V4.8031Z' },
    { paint: 'stroke', d: 'M8.78212 4.8031H1.74988V15.0681H8.78212V4.8031Z' },
    { paint: 'stroke', d: 'M22.2502 4.8031H15.2179V15.0681H22.2502V4.8031Z' },
    { paint: 'stroke', d: 'M8.78212 17.1323H1.74988V19.1968H8.78212V17.1323Z' },
    { paint: 'stroke', d: 'M22.2502 17.1323H15.2179V19.1968H22.2502V17.1323Z' },
    {
      paint: 'stroke',
      d: 'M12.8108 8.48593C12.6281 8.22516 12.1686 8.03136 11.568 8.21253C11.0165 8.37891 10.7694 9.26269 11.4887 9.60239C11.6016 9.65565 11.8907 9.76943 12.3271 9.92396C13.3037 10.2696 13.3357 11.5542 12.1767 11.6556C11.7905 11.6894 11.2398 11.5854 10.9467 11.221',
    },
    { paint: 'stroke', d: 'M11.9523 11.6962V11.9955' },
    { paint: 'stroke', d: 'M11.9523 7.76489V8.12073' },
  ],
  'stat-celebrate': [
    {
      paint: 'stroke',
      d: 'M11.9998 19.7355C14.5426 19.7355 16.6038 17.6743 16.6038 15.1315C16.6038 12.5888 14.5426 10.5275 11.9998 10.5275C9.45708 10.5275 7.39575 12.5888 7.39575 15.1315C7.39575 17.6743 9.45708 19.7355 11.9998 19.7355Z',
    },
    {
      paint: 'stroke',
      d: 'M12.7127 13.5514C12.65 13.4933 12.3202 13.176 11.6818 13.3656C11.1121 13.5529 10.965 14.3241 11.3552 14.6302C11.5792 14.8059 11.9163 14.9504 12.3706 15.111C13.3874 15.4704 13.0115 16.9044 11.9773 16.9112C11.5736 16.9139 11.3849 16.8882 11.0304 16.6563',
    },
    { paint: 'stroke', d: 'M11.9773 17.6439V16.9111' },
    { paint: 'stroke', d: 'M11.9773 13.3096V12.6191' },
    {
      paint: 'stroke',
      d: 'M15.7263 8.01294C18.1219 9.80801 19.7458 12.8055 19.7458 15.9695C19.7458 20.5945 16.2778 22.2502 11.9999 22.2502C7.72209 22.2502 4.25409 20.5945 4.25409 15.9695C4.25409 12.8047 5.87757 9.80801 8.2736 8.01294',
    },
    {
      paint: 'stroke',
      d: 'M9.25794 6.24244L7.60309 1.74988C8.70234 1.74988 8.70234 2.51493 9.80152 2.51493C10.9006 2.51493 10.9006 1.74988 11.9999 1.74988C13.0991 1.74988 13.0991 2.51493 14.1982 2.51493C15.2974 2.51493 15.2974 1.74988 16.3967 1.74988L14.7417 6.24244L18.4903 2.98806',
    },
    { paint: 'stroke', d: 'M19.3279 5.42749L14.7418 6.24233' },
    {
      paint: 'stroke',
      d: 'M8.01629 8.42967C7.29841 8.42967 6.71436 7.84562 6.71436 7.12774C6.71436 6.40986 7.29841 5.82581 8.01629 5.82581H15.7623C16.4802 5.82581 17.0643 6.40986 17.0643 7.12774C17.0643 7.84562 16.4802 8.42967 15.7623 8.42967H8.01629Z',
    },
  ],
  'stat-presentation': [
    {
      paint: 'stroke',
      d: 'M14.3333 19.3288H9.66668C9.66668 18.6922 9.15048 18.176 8.51381 18.176H1.74988V20.195C1.74988 20.6959 2.15601 21.1021 2.65697 21.1021H21.3431C21.844 21.1021 22.2502 20.6959 22.2502 20.195V18.176H15.4862C14.8495 18.176 14.3333 18.6922 14.3333 19.3288Z',
    },
    {
      paint: 'stroke',
      d: 'M9.19417 5.14966H4.09903C3.62549 5.14966 3.24152 5.53358 3.24152 6.00716V17.9676',
    },
    {
      paint: 'stroke',
      d: 'M20.7584 17.9676V6.00717C20.7584 5.53358 20.3745 5.14966 19.9009 5.14966H14.2153',
    },
    { paint: 'stroke', d: 'M6.086 16.5027L9.59666 13.992L13.35 15.347L17.59 11.8328' },
    { paint: 'stroke', d: 'M16.8556 11.7113L17.9142 11.5586L17.6271 12.5881' },
    {
      paint: 'stroke',
      d: 'M16.9471 7.46009C16.9471 10.2098 14.718 12.4389 11.9683 12.4389C9.21865 12.4389 6.9895 10.2098 6.9895 7.46009C6.9895 4.71039 9.21865 2.48132 11.9683 2.48132C14.718 2.48132 16.9471 4.71039 16.9471 7.46009Z',
    },
    {
      paint: 'stroke',
      d: 'M13.0934 5.68773C12.8608 5.35619 12.2757 5.10981 11.5111 5.34015C10.809 5.55165 10.4944 6.67517 11.4102 7.107C11.5539 7.17476 11.922 7.31938 12.4776 7.51576C13.721 7.95526 13.7618 9.58831 12.2861 9.71716C11.7944 9.76011 11.0932 9.62791 10.7202 9.16476',
    },
    { paint: 'stroke', d: 'M12.0007 4.771V10.1491' },
  ],
  'stat-aum': [
    {
      paint: 'stroke',
      d: 'M9.93729 5.52739L9.11932 3.46467C8.79924 2.61114 9.36827 1.75049 10.2929 1.75049H13.7071C14.6317 1.75049 15.2008 2.61114 14.8807 3.46467L14.0627 5.52739',
    },
    {
      paint: 'stroke',
      d: 'M8.85614 5.93286H15.1439C15.6062 5.93286 15.9761 6.30273 15.9761 6.76862C15.9761 7.2345 15.6062 7.60437 15.1439 7.60437H8.85614C8.39381 7.60437 8.02394 7.2345 8.02394 6.76862C8.02394 6.30273 8.39381 5.93286 8.85614 5.93286Z',
    },
    {
      paint: 'stroke',
      d: 'M9.36827 8.01685C6.66541 9.58166 4.84453 13.4012 4.84453 16.353C4.84453 20.3931 8.37248 22.2496 12 22.2496C15.6275 22.2496 19.1555 20.3931 19.1555 16.353C19.1555 13.4012 17.3346 9.58166 14.6317 8.01685',
    },
    {
      paint: 'stroke',
      d: 'M16.602 15.1368C16.602 17.6787 14.5419 19.7388 12 19.7388C9.45806 19.7388 7.39802 17.6787 7.39802 15.1368C7.39802 12.5948 9.45806 10.5348 12 10.5348C14.5419 10.5348 16.602 12.5948 16.602 15.1368Z',
    },
    {
      paint: 'stroke',
      d: 'M13.0758 14.0413C12.7824 13.6501 12.1956 13.4545 11.6088 13.6501C10.9242 13.8457 10.8264 14.8237 11.4132 15.1171C11.7066 15.2149 12.0978 15.4105 12.489 15.5083C13.5648 15.8995 13.467 17.3665 12.2934 17.4643C11.8044 17.4643 11.3154 17.4643 11.022 17.1709',
    },
    { paint: 'stroke', d: 'M12 12.8677V13.5523' },
    { paint: 'stroke', d: 'M12 17.5621V18.2467' },
  ],
  'stat-invest-total': [
    { paint: 'stroke', d: 'M1.75 17.1934H3.98222V21.3845H1.75V17.1934Z' },
    { paint: 'stroke', d: 'M7.43533 21.3844V15.4622' },
    { paint: 'stroke', d: 'M10.8884 13.7584H13.1116V21.3844H10.8884V13.7584Z' },
    { paint: 'stroke', d: 'M16.5647 21.3844V12.0455' },
    { paint: 'stroke', d: 'M20.0178 10.7518H22.25V21.3845H20.0178V10.7518Z' },
    {
      paint: 'stroke',
      d: 'M12 10.9249C14.365 10.9249 16.2822 9.0077 16.2822 6.64269C16.2822 4.27769 14.365 2.36047 12 2.36047C9.63499 2.36047 7.71777 4.27769 7.71777 6.64269C7.71777 9.0077 9.63499 10.9249 12 10.9249Z',
    },
    {
      paint: 'stroke',
      d: 'M13.0022 5.62221C12.7289 5.25777 12.1822 5.07554 11.6355 5.25777C10.9978 5.43999 10.9067 6.3511 11.4533 6.62443C11.7267 6.71554 12.0911 6.89777 12.4555 6.98888C13.4578 7.35332 13.3667 8.71999 12.2733 8.8111C11.8178 8.8111 11.3622 8.8111 11.0889 8.53776',
    },
    { paint: 'stroke', d: 'M12 4.52893V5.16671' },
    { paint: 'stroke', d: 'M12 8.90222V9.54' },
    { paint: 'stroke', d: 'M2.79778 14.3689L9.44888 11.7085' },
    { paint: 'stroke', d: 'M16.1911 9.01157L20.9289 7.11646' },
    { paint: 'stroke', d: 'M19.0247 6.48779L20.9289 7.11646L19.9813 8.88401' },
  ],
  'stat-exit': [
    {
      paint: 'stroke',
      d: 'M12 16.0979C14.2632 16.0979 16.0978 14.2632 16.0978 12C16.0978 9.73688 14.2632 7.90222 12 7.90222C9.73685 7.90222 7.90219 9.73688 7.90219 12C7.90219 14.2632 9.73685 16.0979 12 16.0979Z',
    },
    {
      paint: 'stroke',
      d: 'M13.0244 10.9755C12.745 10.603 12.1863 10.4167 11.6275 10.603C10.9755 10.7892 10.8824 11.7206 11.4412 12C11.7206 12.0931 12.0931 12.2794 12.4656 12.3725C13.4901 12.745 13.397 14.142 12.2794 14.2351C11.8137 14.2351 11.3481 14.2351 11.0687 13.9557',
    },
    { paint: 'stroke', d: 'M12 9.85791V10.5098' },
    { paint: 'stroke', d: 'M12 14.3282V14.9802' },
    {
      paint: 'stroke',
      d: 'M1.85791 13.425C2.18028 15.7112 3.26546 17.8214 4.93749 19.4136C6.60951 21.0057 8.77033 21.9864 11.0696 22.1966C13.3688 22.4067 15.6716 21.8341 17.6046 20.5715C19.5375 19.3088 20.9873 17.4303 21.7188 15.2404C22.4504 13.0506 22.4207 10.6778 21.6347 8.50692C20.8487 6.33602 19.3524 4.49429 17.3885 3.28036C15.4246 2.06642 13.1082 1.55148 10.8149 1.81903C8.52168 2.08658 6.38604 3.12094 4.75432 4.75437',
    },
    { paint: 'stroke', d: 'M4.7543 2.14661V4.75431H7.362' },
  ],
  'stat-valuation': [
    {
      paint: 'stroke',
      d: 'M15.4103 6.06275C15.4103 7.93901 13.8887 9.46059 12.0124 9.46059C10.1362 9.46059 8.61459 7.93901 8.61459 6.06275C8.61459 4.18649 10.1362 2.66492 12.0124 2.66492C13.8887 2.66492 15.4103 4.18649 15.4103 6.06275Z',
    },
    {
      paint: 'stroke',
      d: 'M12.7218 5.08729C12.7218 5.47921 12.4043 5.79665 12.0124 5.79665C11.6205 5.79665 11.303 5.47921 11.303 5.08729C11.303 4.69537 11.6205 4.37793 12.0124 4.37793C12.4043 4.37793 12.7218 4.69537 12.7218 5.08729Z',
    },
    {
      paint: 'stroke',
      d: 'M10.505 8.10203C10.771 7.48134 11.3474 7.21533 12.0124 7.21533C12.6774 7.21533 13.2538 7.48134 13.5198 8.10203',
    },
    {
      paint: 'stroke',
      d: 'M8.57024 17.9089C8.57024 19.7852 7.04866 21.3068 5.1724 21.3068C3.29614 21.3068 1.77457 19.7852 1.77457 17.9089C1.77457 16.0327 3.29614 14.5111 5.1724 14.5111C7.04866 14.5111 8.57024 16.0327 8.57024 17.9089Z',
    },
    {
      paint: 'stroke',
      d: 'M5.88176 16.9336C5.88176 17.3255 5.56432 17.643 5.1724 17.643C4.78048 17.643 4.46304 17.3255 4.46304 16.9336C4.46304 16.5417 4.78048 16.2242 5.1724 16.2242C5.56432 16.2242 5.88176 16.5417 5.88176 16.9336Z',
    },
    {
      paint: 'stroke',
      d: 'M3.66504 19.9483C3.93105 19.3277 4.5074 19.0616 5.17243 19.0616C5.83745 19.0616 6.41381 19.3277 6.67982 19.9483',
    },
    {
      paint: 'stroke',
      d: 'M22.2503 17.9089C22.2503 19.7852 20.7287 21.3068 18.8524 21.3068C16.9762 21.3068 15.4546 19.7852 15.4546 17.9089C15.4546 16.0327 16.9762 14.5111 18.8524 14.5111C20.7287 14.5111 22.2503 16.0327 22.2503 17.9089Z',
    },
    {
      paint: 'stroke',
      d: 'M19.5618 16.9336C19.5618 17.3255 19.2443 17.643 18.8524 17.643C18.4605 17.643 18.1431 17.3255 18.1431 16.9336C18.1431 16.5417 18.4605 16.2242 18.8524 16.2242C19.2443 16.2242 19.5618 16.5417 19.5618 16.9336Z',
    },
    {
      paint: 'stroke',
      d: 'M17.345 19.9483C17.611 19.3277 18.1874 19.0616 18.8524 19.0616C19.5174 19.0616 20.0938 19.3277 20.3598 19.9483',
    },
    {
      paint: 'stroke',
      d: 'M13.2609 13.9649C13.2609 14.6548 12.7022 15.2134 12.0124 15.2134C11.3225 15.2134 10.7639 14.6548 10.7639 13.9649C10.7639 13.2751 11.3225 12.7164 12.0124 12.7164C12.7022 12.7164 13.2609 13.2751 13.2609 13.9649Z',
    },
    { paint: 'stroke', d: 'M12.0124 9.46045V12.7164' },
    { paint: 'stroke', d: 'M10.9306 14.5883L8.11624 16.2118' },
    { paint: 'stroke', d: 'M13.0942 14.5883L15.9086 16.2118' },
    { paint: 'stroke', d: 'M8.75825 7.0415C5.97755 8.33254 4.10484 11.3686 4.39568 14.6033' },
    { paint: 'stroke', d: 'M15.2631 7.0415C18.0508 8.33254 19.9165 11.3686 19.6256 14.6033' },
    { paint: 'stroke', d: 'M7.64453 20.2356C10.0776 22.0374 13.9436 22.0374 16.3768 20.2356' },
  ],
  info: [
    {
      paint: 'stroke',
      d: 'M20.25 12C20.25 16.556 16.556 20.25 12 20.25C7.444 20.25 3.75 16.556 3.75 12C3.75 7.444 7.444 3.75 12 3.75C16.556 3.75 20.25 7.444 20.25 12Z',
    },
    { paint: 'stroke', d: 'M12 11V16.25' },
    { paint: 'stroke', d: 'M11.995 7.75H12.005' },
  ],
  'alert-circle': [
    {
      paint: 'stroke',
      d: 'M20.25 12C20.25 16.556 16.556 20.25 12 20.25C7.444 20.25 3.75 16.556 3.75 12C3.75 7.444 7.444 3.75 12 3.75C16.556 3.75 20.25 7.444 20.25 12Z',
    },
    { paint: 'stroke', d: 'M12 7.75V12.75' },
    { paint: 'stroke', d: 'M11.995 16.25H12.005' },
  ],
  'alert-triangle': [
    {
      paint: 'stroke',
      d: 'M10.268 4.5L2.74199 17.5C2.38499 18.119 2.38499 18.881 2.74199 19.5C3.09899 20.119 3.75899 20.5 4.47399 20.5H19.526C20.241 20.5 20.901 20.119 21.258 19.5C21.615 18.881 21.615 18.119 21.258 17.5L13.732 4.5C13.375 3.881 12.714 3.5 12 3.5C11.286 3.5 10.625 3.881 10.268 4.5Z',
    },
    { paint: 'stroke', d: 'M12 9.5V13.5' },
    { paint: 'stroke', d: 'M11.995 16.75H12.005' },
  ],
  inbox: [
    {
      paint: 'stroke',
      d: 'M21.25 12.5L18.3 5.30005C17.995 4.54605 17.263 4.05205 16.45 4.05005H7.55C6.737 4.05205 6.005 4.54605 5.7 5.30005L2.75 12.5V17.95C2.75 19.055 3.645 19.95 4.75 19.95H19.25C20.355 19.95 21.25 19.055 21.25 17.95V12.5Z',
    },
    { paint: 'stroke', d: 'M2.75 12.5H7.75L9.25 15H14.75L16.25 12.5H21.25' },
  ],
  image: [
    {
      paint: 'stroke',
      d: 'M5.75 3.75H18.25C19.355 3.75 20.25 4.645 20.25 5.75V18.25C20.25 19.355 19.355 20.25 18.25 20.25H5.75C4.645 20.25 3.75 19.355 3.75 18.25V5.75C3.75 4.645 4.645 3.75 5.75 3.75Z',
    },
    {
      paint: 'stroke',
      d: 'M10.75 9C10.75 9.966 9.966 10.75 9 10.75C8.034 10.75 7.25 9.966 7.25 9C7.25 8.034 8.034 7.25 9 7.25C9.966 7.25 10.75 8.034 10.75 9Z',
    },
    {
      paint: 'stroke',
      d: 'M20.25 15L17.414 12.164C16.633 11.383 15.367 11.383 14.586 12.164L6.5 20.25',
    },
  ],
  clock: [
    {
      paint: 'stroke',
      d: 'M20.25 12C20.25 16.556 16.556 20.25 12 20.25C7.444 20.25 3.75 16.556 3.75 12C3.75 7.444 7.444 3.75 12 3.75C16.556 3.75 20.25 7.444 20.25 12Z',
    },
    { paint: 'stroke', d: 'M12 7.5V12L15 13.75' },
  ],
  bold: [
    {
      paint: 'stroke',
      d: 'M7 4.75H13C15.071 4.75 16.75 6.429 16.75 8.5C16.75 10.571 15.071 12.25 13 12.25H7V4.75Z',
    },
    {
      paint: 'stroke',
      d: 'M7 12.25H14C15.933 12.25 17.5 13.817 17.5 15.75C17.5 17.683 15.933 19.25 14 19.25H7V12.25Z',
    },
  ],
  italic: [
    { paint: 'stroke', d: 'M10 4.75H18' },
    { paint: 'stroke', d: 'M6 19.25H14' },
    { paint: 'stroke', d: 'M14.5 4.75L9.5 19.25' },
  ],
  'list-bullet': [
    { paint: 'stroke', d: 'M9 6.5H20.25' },
    { paint: 'stroke', d: 'M9 12H20.25' },
    { paint: 'stroke', d: 'M9 17.5H20.25' },
    { paint: 'stroke', d: 'M4.495 6.5H4.505' },
    { paint: 'stroke', d: 'M4.495 12H4.505' },
    { paint: 'stroke', d: 'M4.495 17.5H4.505' },
  ],
  'list-number': [
    { paint: 'stroke', d: 'M10 6.5H20.25' },
    { paint: 'stroke', d: 'M10 12H20.25' },
    { paint: 'stroke', d: 'M10 17.5H20.25' },
    { paint: 'stroke', d: 'M4 5L5.25 4.25V9' },
    { paint: 'stroke', d: 'M4 9H6.5' },
    {
      paint: 'stroke',
      d: 'M4 16.25C4 15.56 4.56 15 5.25 15C5.94 15 6.5 15.56 6.5 16.25C6.5 17.6 4 18.4 4 20.25H6.6',
    },
  ],
  link: [
    {
      paint: 'stroke',
      d: 'M10 13.0001C10.869 14.1621 12.2 14.8891 13.647 14.9921C15.094 15.0961 16.514 14.5661 17.54 13.5401L20.54 10.5401C22.435 8.5781 22.408 5.4601 20.479 3.5311C18.55 1.6021 15.432 1.5751 13.47 3.4701L11.75 5.1801',
    },
    {
      paint: 'stroke',
      d: 'M14 11C13.131 9.83799 11.8 9.11099 10.353 9.00799C8.90603 8.90399 7.48603 9.43399 6.46003 10.46L3.46003 13.46C1.56503 15.422 1.59203 18.54 3.52103 20.469C5.45003 22.398 8.56803 22.425 10.53 20.53L12.24 18.82',
    },
  ],
};
